import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { LoadingController, ModalController, NavParams, ToastController } from '@ionic/angular';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter } from 'rxjs/operators';
import { CycleUser, CycleUserStatus } from 'src/app/performance-cycles/_interfaces/cycle-user.interface';
import { PerformanceCycleService } from 'src/app/performance-cycles/performance-cycles.service';
import { BaseClass } from '../../classes/base';
import { ProcessStatusEnum, ProcessTypeEnum } from '../../interfaces/process.interface';
import { AuthService, LoginResponse } from '../../services/auth.service';
import { TranslationService } from '../../services/translation.service';
import { UtilService } from '../../services/util.service';
import { UserRoleEnum } from '../../enums/UserRoleEnum';
import { CyclePreview, CycleProcess } from '../../interfaces/cycle.interface';

@Component({
    selector: 'app-process-user-list-modal',
    templateUrl: './process-user-list.modal.html'
})
export class ProcessUserListModal extends BaseClass implements OnInit, OnDestroy {

    public process: CycleProcess;
    public cycle: CyclePreview;

    public attributeIds: number[] = [];
    public directSubordinates: boolean;
    public userStatus: number;

    public users: Array<CycleUser> = [];
    public CycleUserStatus = CycleUserStatus;
    public hasLoadMore: boolean = false;
    public hasImpersonate: boolean = false;
    public hasControlProcess: boolean = false;
    public isAdmin: boolean = false;

    private _searchSubject: Subject<string> = new Subject();
    private _currentPage: number = 1;
    private _searchValue: string;
    private readonly _pageSize: number = 40;
    public userId: string;

    constructor(
        protected _toastController: ToastController,
        protected _translationService: TranslationService,
        private _loadingController: LoadingController,
        private _modalController: ModalController,
        private _navParams: NavParams,
        private _router: Router,
        private _authService: AuthService,
        private _performanceCycleService: PerformanceCycleService,
        private _utilService: UtilService
    ) {
        super(_toastController, _translationService);
        this.process = this._navParams.get('process');
        this.cycle = this._navParams.get('cycle');
        this.attributeIds = this._navParams.get('attributeIds');
        this.directSubordinates = this._navParams.get('directSubordinates');
        this.userStatus = this._navParams.get('userStatus');
    }

    ngOnInit() {
        this._loadUsers(
            this.process.id,
            this.process.cycleProcessConfigId,
            this.process.cycleId,
            this._currentPage, ''
        );
        this._setSearchSubscription();
        const currentUser = this._authService.getLoggedUser();
        if (currentUser && currentUser.roles && currentUser.roles.length > 0) {
            this.userId = currentUser.user_id;
            this.hasImpersonate = currentUser.roles.some((s) => s == UserRoleEnum.Impersonate);
            this.isAdmin = currentUser.roles.some((s) => s == UserRoleEnum.Administrator);
            this.hasControlProcess = currentUser.roles.some((s) => s == UserRoleEnum.HR);
        }
    }

    public resolveProcess(user: CycleUser): void {
        const closingProcess = this.isOpen(user.status);
        closingProcess ?
            this._closeProcess(user) :
            this._reopenProcess(user);
    }

    private _closeProcess(user: CycleUser): void {
        this._performanceCycleService.closeProcessForUser(
            this.process.type, user.cycleParticipantId, this.process.cycleProcessConfigId, this.cycle.closed
        ).subscribe(() => {
            this.notify(
                this._translationService.getTranslation(
                    'performanceCycles.processClosed'
                ), 'success'
            );
            user.status = ProcessStatusEnum.AllowedViewing;
        }, (error) => this.notify(this.getErrorNotification(error), 'danger'));
    }

    private _reopenProcess(user: CycleUser): void {
        let isFromUserList = true;
        this._performanceCycleService.reopenProcessForUser(
            this.process.type, user.cycleParticipantId, this.process.cycleProcessConfigId, this.cycle.closed, isFromUserList
        ).subscribe(() => {
            this.notify(
                this._translationService.getTranslation(
                    'performanceCycles.processReopened'
                ), 'success'
            );
            user.status = ProcessStatusEnum.WaitingInput;
        }, (error) => this.notify(this.getErrorNotification(error), 'danger'));
    }

    public enableStep(user: CycleUser) {
        this._performanceCycleService.enableStepForUser(this.process.type, user.cycleParticipantId, this.process.cycleProcessConfigId, this.cycle.closed).subscribe(() => {
            this.notify(
                this._translationService.getTranslation(
                    'performanceCycles.stepEnabled'
                ), 'success'
            );
            user.status = ProcessStatusEnum.WaitingInput;
        }, (error) => this.notify(this.getErrorNotification(error), 'danger'));
    }

    public impersonateUser(user: CycleUser, redirectType: string): void {
        this._performanceCycleService.impersonateUser(user.userId).subscribe(res => {
            if (res.success) {
                let response = res.data;
                this._authService.setImpersonationInfo({
                    'userId': user.userId,
                    'username': user.name,
                    'imageUrl': user.imageUrl,
                    roles: response.roles,
                });

                let actualAuth = this._authService.getLoggedUser();

                let newAuth: LoginResponse = {
                    'Name': undefined,
                    'UserId': undefined,
                    'completed_registration': undefined,
                    'email_verified': undefined,
                    'first_access': undefined,
                    'imageUrl': undefined,
                    'refresh_token': undefined,
                    'role': undefined,
                    'roles': actualAuth.roles,
                    'sso': undefined,
                    'access_token': response.tokenResource.accessToken,
                    'username': actualAuth.username,
                    'user_id': actualAuth.user_id,
                    'name': actualAuth.name,
                    'expires_in': undefined,
                    'country': actualAuth.country,
                };

                this._authService.setLoggedUser(newAuth).then(async _ => {
                    if (redirectType === 'dashboard') {
                        await window.location.reload();
                        window.location.href = '/dashboard';
                    } else {
                        this._redirectByProcess(
                            this.process.type, this.process.cycleId, user.userId
                        );
                    }
                });
            }
        }, error => {
            console.log(error)
        });

    }

    public getColorByStatus(status: ProcessStatusEnum): string {
        switch (status) {
            case ProcessStatusEnum.Declined:
                return 'warning';
            case ProcessStatusEnum.AllowedViewing:
            case ProcessStatusEnum.AllowedViewingWithWarnings:
                return 'success';
            case ProcessStatusEnum.NotEnabled:
            case ProcessStatusEnum.WaitingInput:
                return 'primary';
            default:
                break;
        }
    }

    public isOpen(status: ProcessStatusEnum): boolean {
        return status === ProcessStatusEnum.WaitingInput ||
            status === ProcessStatusEnum.Declined;
    }

    public updateSearch(searchTextValue: string) {
        this._searchSubject.next(searchTextValue);
    }

    public loadMore(): void {
        this._currentPage++;
        this._loadUsers(
            this.process.id,
            this.process.cycleProcessConfigId,
            this.process.cycleId,
            this._currentPage,
            this._searchValue
        );
    }

    public dismiss(): void {
        this._modalController.dismiss();
    }

    private async _loadUsers(
        processId: number, cycleProcessConfigId: number, cycleId: number, page: number, searchValue: string
    ): Promise<void> {
        let loading = null;

        if (!searchValue) {
            loading = await this._loadingController.create();
            await loading.present();
        }

        this._performanceCycleService.getCycleSubordinates(
            processId, cycleProcessConfigId, cycleId, page, this._pageSize, searchValue, this.directSubordinates, this.userStatus, this.attributeIds
        ).subscribe(async (response) => {
            this.users = this._currentPage === 1 ?
                response.data : [...this.users, ...response.data];

            this.hasLoadMore = response.data.length === this._pageSize;
            if (loading)
                loading.dismiss();

        }, (error) => {
            if (loading)
                loading.dismiss();
            this.notify(this.getErrorNotification(error), 'danger');
        });
    }

    private _redirectByProcess(processType: ProcessTypeEnum, cycleId: number, userId: string): void {
        switch (processType) {
            case ProcessTypeEnum.GoalInputs:
                this._router.navigate(['goals-input/' + cycleId]).then(() => {
                    window.location.reload();
                });
                break;
            case ProcessTypeEnum.GoalInputValidation:
                this._router.navigate(['goals-validation/' + cycleId]).then(() => {
                    window.location.reload();
                });
                break;
            case ProcessTypeEnum.FreeEvaluationMidYear:
                this._router.navigate(['free-evaluation-mid-year/' + cycleId]).then(() => {
                    window.location.reload();
                });
                break;
            case ProcessTypeEnum.GoalsReview:
                this._router.navigate(['goals-review/' + cycleId]).then(() => {
                    window.location.reload();
                });
                break;
            case ProcessTypeEnum.GoalsReviewValidation:
                this._router.navigate(['goals-review-validation/' + cycleId]).then(() => {
                    window.location.reload();
                });
                break;
            case ProcessTypeEnum.ThreeSixtyEvaluatorsSelection:
                this._router.navigate(['360-evaluation/selection/' + cycleId]).then(() => {
                    window.location.reload();
                });
                break;
            case ProcessTypeEnum.ThreeSixtyEvaluatorsValidation:
                this._router.navigate(['360-evaluation/validation/' + cycleId]).then(() => {
                    window.location.reload();
                });
                break;
            case ProcessTypeEnum.ThreeSixtyEvaluation:
                const stateUrl = this._utilService.setUrlStateGuard(ProcessTypeEnum.ThreeSixtyEvaluation, null, this.process.id);
                this._router.navigate(['360-evaluation/evaluation/' + cycleId], { state: stateUrl }).then(() => {
                });
                break;
            case ProcessTypeEnum.SelfEvaluation:
                this._router.navigate(['self-evaluation/' + cycleId]).then(() => {
                    window.location.reload();
                });
                break;
            case ProcessTypeEnum.ManagerEvaluation:
                this._router.navigate(['manager-evaluation/' + cycleId]).then(() => {
                    window.location.reload();
                });
                break;
            case ProcessTypeEnum.FreeEvaluationEndYear:
                this._router.navigate(['free-evaluation-end-year/' + cycleId]).then(() => {
                    window.location.reload();
                });
                break;
            case ProcessTypeEnum.EvaluationCalibration:
                this._router.navigate(['groups/' + cycleId + '/' + this.process.id]).then(() => {
                    window.location.reload();
                });
                break;
            case ProcessTypeEnum.FinalResult:
                this._router.navigate(['final-report/' + cycleId + '/' + userId]).then(() => {
                    window.location.reload();
                });
                break;
            default:
                break;
        }
    }

    private _setSearchSubscription() {
        this._searchSubject.pipe(
            distinctUntilChanged(),
            debounceTime(1000),
            filter((searchValue: string) => !!searchValue && searchValue.trim().length > 0)
        ).subscribe((searchValue: string) => {
            this._searchValue = searchValue;
            this._currentPage = 1;
            this._loadUsers(
                this.process.id,
                this.process.cycleProcessConfigId,
                this.process.cycleId,
                this._currentPage,
                searchValue
            );
        });
    }

    ngOnDestroy() {
        this._searchSubject.unsubscribe();
    }

}
